import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { LogIn, Loader2 } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "admin") {
        // Admins must use the dedicated /admin/login route
        toast.error("Admins, please use the admin login page.");
        nav("/admin/login");
        return;
      }
      toast.success(`Welcome back, ${user.name}!`);
      nav(user.role === "organizer" ? "/organizer" : from);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="glass rounded-3xl p-8">
          <div className="inline-flex rounded-full bg-lime text-bg px-3 py-1 font-mono text-[10px] uppercase tracking-widest mb-4">Log In</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.9]">Welcome back to the <span className="grad-text-hot">crowd.</span></h1>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input required data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark" />
            <input required data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark" />
            <button data-testid="login-submit" disabled={loading} className="btn-primary w-full justify-center">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} {loading ? "Signing in..." : "Log In"}
            </button>
          </form>
          <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted">
            <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
          </div>
          <GoogleSignInButton testId="login-google" />
          <div className="mt-6 flex items-center justify-between text-sm font-mono">
            <Link to="/forgot-password" data-testid="login-forgot" className="text-muted hover:text-lime underline">Forgot password?</Link>
            <Link to="/signup" data-testid="login-goto-signup" className="text-magenta font-bold underline">Sign up →</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
